import { Badge, IconButton } from "@mui/material";
import { uniqBy } from "lodash";
import { useCallback, useEffect, useState } from "react";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useGetAllNotifications } from "../../../../queries/notification/use-get-all-notification";
import { useReadAllNotification } from "../../../../queries/notification/use-read-all-notification";
import { useReadNotification } from "../../../../queries/notification/use-read-notification-by-id";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../utils/utils";
import { BellIcon } from "../../../svg-icons/material-icons";
import NotificationDrawer from "./NotificationDrawer";
import { viewedMapper } from "./notification-constant";

const Notification = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [activeTab, setActiveTab] = useState<string>("unRead");
  const disclosureHost = useDisclosure(false, {
    onClose: () => setActiveTab("unRead"),
  });
  const showSnackbar = useSnackbar();
  const {
    data: notificationData,
    refetch,
    isFetching,
  } = useGetAllNotifications({
    pagination: pagination,
    viewed: viewedMapper[activeTab],
    xTENANTID: getXtenantId(),
    enabled: disclosureHost.opened,
  });
  const { mutateAsync: markRead } = useReadNotification();
  const { mutateAsync: markAllRead } = useReadAllNotification();

  const [notifications, setNotifications] = useState(
    notificationData?.data?.content ?? []
  );
  const count = notificationData?.data?.page?.totalElements ?? 0;

  useEffect(() => {
    const content = notificationData?.data?.content ?? [];
    // Accumulate pages for infinite scroll. Page 0 is a fresh list (initial
    // load, tab change, drawer reopen, refetch after read); later pages are
    // appended and de-duped by uuid so the list grows instead of being
    // replaced. Replacing made the list keep the same height near the bottom,
    // which re-triggered the scroll threshold and cascaded straight to the
    // last records.
    setNotifications((prev) =>
      pagination.page === 0
        ? content
        : uniqBy([...(prev ?? []), ...content], "uuid")
    );
  }, [notificationData]);

  const onRead = (notificationId: string, ind: number) => {
    /**
     * change particular index notification read before API call
     */
    // setIndNotificationRead(ind, true);

    /**
     * api call at background (will not affect the instant change of optimistic approach)
     */
    markRead({
      pushNotificationId: notificationId,
      xTENANTID: getXtenantId(),
    })
      .then(() => refetch())
      .catch((err) => {
        /**
         * reset optimistic approach change for API fail
         */
        setIndNotificationRead(ind, false);
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const onMarkAllRead = () => {
    showToastOnApiCall({
      apiCall: markAllRead({
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
      onSave: () => {
        refetch();
      },
    });
  };

  const setIndNotificationRead = (ind: number, view: boolean) => {
    if (activeTab === "unRead") {
      setNotifications((prev) => prev.toSpliced(ind, 1));
    } else
      setNotifications((prev) =>
        prev.toSpliced(ind, 1, { ...prev[ind], viewed: view })
      );
  };

  // console.log("notifications", notifications);

  const [atEnd, setAtEnd] = useState<boolean>(false);
  const { onScrollIntersect } = useScrollOnIntersect({
    isFetching,
    atEnd,
    pagination,
    onLoadMore: () => {
      // Stop once every available record is already loaded. Without this guard
      // a partial last page (e.g. 11 items with pageSize 20) still triggered a
      // fetch for the next page, which returned empty/error and blanked the list.
      const totalElements = notificationData?.data?.page?.totalElements ?? 0;
      const loadedCount = (pagination.page + 1) * pagination.pageSize;
      if (loadedCount >= totalElements) {
        // setAtEnd(notificationData?.data?.content?.length === 0);
        setAtEnd(true);
        return;
      }
      setPagination({ ...pagination, page: pagination.page + 1 });
    },
  });

  return (
    <>
      <IconButton
        sx={{ color: "#fff" }}
        onClick={() => {
          setPagination(DEF_PAGINATION);
          setAtEnd(false);
          setNotifications([]);
          disclosureHost.open(count);
          refetch();
        }}
      >
        <Badge
          variant="dot"
          badgeContent={count}
          color="success"
          sx={{
            "& .MuiBadge-badge": {
              top: "-0px",
              right: "-2px",
            },
          }}
        >
          <BellIcon />
        </Badge>
        {/* <img src="/notification.svg" alt="notifications" /> */}

        {/* <Chip
          sx={{
            background: NOTIFICATION_COLORS.BADGE_TEXT,
            width: "8px",
            height: "8px",
            position: "absolute",
            top: "5px",
            right: "4px",
          }}
        />
      </IconButton> */}
      </IconButton>

      <NotificationDrawer
        onTabChange={() => {
          setPagination(DEF_PAGINATION);
          setAtEnd(false);
          setNotifications([]);
        }}
        isLoading={isFetching}
        fetchMoreOnBottomReached={onScrollIntersect}
        notificationCount={count}
        onRead={onRead}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        disclosureHost={disclosureHost}
        allNotifications={notifications}
        onMarkAllRead={onMarkAllRead}
      />
    </>
  );
};

export default Notification;

interface useScrollOnIntersectProps {
  isFetching: boolean;
  pagination: DGT_Pagination;
  atEnd: boolean;
  onLoadMore: () => void;
}

const useScrollOnIntersect = ({
  isFetching,
  atEnd,
  pagination,
  onLoadMore,
}: useScrollOnIntersectProps) => {
  const fetchOnTargetObserve = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement && !isFetching && !atEnd) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight <= 13) onLoadMore();
      }
    },
    [isFetching, atEnd, pagination, onLoadMore]
  );

  return { onScrollIntersect: fetchOnTargetObserve };
};

// const MAX_COUNT = 9999;
